import React, { useEffect, startTransition, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import css from "./RequiredAuth.module.css";
import { ROLES } from "../../constants/roles";
import { useRoleAccess } from "../../hooks/useRoleAccess";
import { authActions } from "../../store/slices/auth-slices";
import { getConfig } from "../../config";

const RequiredAuth = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const config = getConfig();
  const { isAuthenticated, initializationStatus, user } = useSelector(
    (state) => state.auth
  );
  const hasRedirected = useRef(false);
  const { hasAnyRole } = useRoleAccess();

  // Handle redirect to login when not authenticated
  useEffect(() => {
    if (
      !hasRedirected.current &&
      initializationStatus !== "loading" &&
      !isAuthenticated
    ) {
      hasRedirected.current = true;
      startTransition(() => {
        navigate("/login", { replace: true });
      });
    }

    // Reset flag when authenticated
    if (isAuthenticated) {
      hasRedirected.current = false;
    }
  }, [isAuthenticated, initializationStatus, navigate]);

  const handleLogout = () => {
    dispatch(authActions.logout()).unwrap().then(() => {
      startTransition(() => {
        navigate("/login");
      });
    });
  };

  // Show loading state while checking authentication
  if (initializationStatus === "loading") {
    return (
      <div className={css.loadingWrap}>
        <div className={css.spinner} />
        <span>Validating authentication...</span>
      </div>
    );
  }

  // If not authenticated, show message while redirecting
  if (!isAuthenticated) {
    return (
      <div className={css.loadingWrap}>
        <div className={css.spinner} />
        <span>Redirecting to login...</span>
      </div>
    );
  }

  // Check if user role is not allowed
  if (
    user &&
    user.role &&
    !hasAnyRole([ROLES.ADMIN, ROLES.MANAGER, ROLES.COURIER, ROLES.NUTRITIONIST])
  ) {
    return (
      <div className={css.fullPage}>
        <div className={css.card}>
          <div className={css.logoArea}>
            <img src={config.logo} alt={config.title} className={css.logo} />
          </div>

          <div className={css.iconWrap}>
            <div className={css.iconCircle}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
          </div>

          <h1 className={css.title}>Not Authorized</h1>
          <p className={css.message}>
            Your account doesn't have permission to access this admin panel.
            Please contact your administrator to request access.
          </p>

         
          <button className={css.logoutButton} onClick={handleLogout}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // User is authenticated and has access
  return children;
};

export { RequiredAuth };
